import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const fadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

const popIn = keyframes`
	from {
		opacity: 0;
		transform: scale(0.96);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
`;

const menuSurfaceStyles = css`
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 18px;
	background:
		radial-gradient(circle at 70% 0%, rgba(255, 211, 107, 0.08), transparent 36%),
		radial-gradient(circle at 18% 12%, rgba(176, 38, 255, 0.16), transparent 34%),
		rgba(14, 8, 20, 0.96);
	box-shadow:
		0 28px 72px rgba(0, 0, 0, 0.56),
		0 0 28px rgba(176, 38, 255, 0.12);
	backdrop-filter: blur(22px) saturate(1.1);
`;

export const Wrap = styled.div`
	position: relative;
`;

export const Menu = styled.div`
	position: absolute;
	top: calc(100% + 14px);
	right: 0;
	z-index: 30;
	width: min(320px, calc(100vw - 28px));
	${menuSurfaceStyles}
	opacity: ${(props) => (props.$open ? 1 : 0)};
	pointer-events: ${(props) => (props.$open ? 'auto' : 'none')};
	transform: translateY(${(props) => (props.$open ? '0' : '-6px')});
	transition: opacity 0.16s ease, transform 0.16s ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: none;
	}
`;

export const MobileOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 6000;
	display: grid;
	place-items: center;
	padding:
		max(16px, env(safe-area-inset-top, 0px))
		max(16px, env(safe-area-inset-right, 0px))
		max(16px, env(safe-area-inset-bottom, 0px))
		max(16px, env(safe-area-inset-left, 0px));
	background: rgba(0, 0, 0, 0.72);
	backdrop-filter: blur(10px);
	animation: ${fadeIn} 0.18s ease;
`;

export const MobilePanel = styled.div`
	position: relative;
	width: min(100%, 420px);
	max-height: min(88vh, calc(100dvh - 32px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)));
	overflow: auto;
	-webkit-overflow-scrolling: touch;
	${menuSurfaceStyles}
	animation: ${popIn} 0.22s ease;
`;

export const CloseButton = styled.button`
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 2;
	width: 36px;
	height: 36px;
	border: 0;
	border-radius: 50%;
	display: grid;
	place-items: center;
	background: rgba(255, 255, 255, 0.08);
	color: rgba(255, 247, 255, 0.92);
	font-size: 1.5rem;
	line-height: 1;
	cursor: pointer;
`;

export const Head = styled.section`
	min-height: 96px;
	display: grid;
	grid-template-columns: 54px minmax(0, 1fr);
	align-items: center;
	gap: 14px;
	padding: 20px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 52px 20px 20px;
	}
`;

export const Avatar = styled.div`
	width: 54px;
	height: 54px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	overflow: hidden;
	border: 1px solid rgba(198, 128, 255, 0.54);
	color: #c680ff;
	background: rgba(255, 255, 255, 0.035);
	box-shadow: 0 0 22px rgba(176, 38, 255, 0.18);
	font-weight: 800;
	font-size: 1.1rem;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

export const Identity = styled.div`
	display: grid;
	gap: 4px;
	min-width: 0;

	input {
		width: 100%;
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 8px;
		padding: 8px 10px;
		background: rgba(255, 255, 255, 0.04);
		color: ${THEME.text};
		font-family: ${THEME.fontApp};
		font-size: 0.95rem;
		transition:
			border-color 160ms ease,
			box-shadow 160ms ease;

		&:focus {
			outline: none;
			border-color: rgba(255, 77, 242, 0.45);
			box-shadow: 0 0 0 2px rgba(255, 77, 242, 0.12);
		}

		&:disabled {
			opacity: 0.7;
			cursor: wait;
		}
	}

	span {
		display: block;
		color: ${THEME.soft};
		font-size: 0.82rem;
		line-height: 1.35;
		overflow-wrap: anywhere;
		word-break: break-word;
	}
`;

export const NameHint = styled.span`
	font-size: 0.68rem !important;
	color: ${(props) => (props.$error ? '#ff8f9d' : THEME.soft)} !important;
`;

export const Stats = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	margin: 0 20px 14px;
	padding: 12px;
	border-radius: 12px;
	background: rgba(255, 255, 255, 0.04);
`;

export const Stat = styled.div`
	display: grid;
	gap: 4px;
	text-align: center;

	span:first-child {
		font-size: 0.68rem;
		text-transform: uppercase;
		color: ${THEME.soft};
	}

	strong {
		color: ${THEME.gold};
		font-size: 1rem;
	}
`;

export const Logout = styled.button`
	width: 100%;
	min-height: 54px;
	border: 0;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
	padding: 0 20px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	background: transparent;
	color: rgba(255, 247, 255, 0.92);
	font-family: ${THEME.fontApp};
	font-weight: 800;
	cursor: pointer;

	&:hover:not(:disabled) {
		background: rgba(255, 255, 255, 0.055);
	}

	&:disabled {
		opacity: 0.85;
		cursor: wait;
	}
`;

export const LogoutSpinner = styled.span`
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, 0.35);
	border-top-color: #fff;
	border-radius: 50%;
	animation: profileLogoutSpin 0.8s linear infinite;

	@keyframes profileLogoutSpin {
		to {
			transform: rotate(360deg);
		}
	}
`;

export const Links = styled.footer`
	padding: 14px 16px 16px;
	border-top: 1px solid rgba(255, 255, 255, 0.08);
	background:
		linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(0, 0, 0, 0.14));
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

export const LegalGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 8px;
`;

export const LegalLink = styled.a`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 40px;
	padding: 8px 10px;
	border-radius: 10px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	background: rgba(255, 255, 255, 0.035);
	color: rgba(225, 205, 235, 0.82);
	font-family: ${THEME.fontApp};
	font-size: 0.68rem;
	font-weight: 700;
	line-height: 1.3;
	text-align: center;
	text-decoration: none;
	transition:
		border-color 160ms ease,
		color 160ms ease,
		background 160ms ease,
		box-shadow 160ms ease;

	&:hover {
		border-color: rgba(255, 77, 242, 0.38);
		color: ${THEME.gold};
		background: rgba(255, 77, 242, 0.08);
		box-shadow: 0 0 16px rgba(255, 77, 242, 0.1);
	}
`;

export const SupportCopy = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

export const SupportLabel = styled.span`
	font-family: ${THEME.fontApp};
	font-size: 0.62rem;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: ${THEME.soft};
	line-height: 1.2;
`;

export const SupportEmail = styled.span`
	font-family: ${THEME.fontApp};
	font-size: 0.76rem;
	font-weight: 700;
	line-height: 1.35;
	color: ${THEME.text};
	overflow-wrap: anywhere;
	word-break: break-word;
	transition: color 160ms ease;
`;

export const SupportRow = styled.a`
	display: flex;
	align-items: center;
	gap: 10px;
	min-height: 44px;
	padding: 10px 12px;
	border-radius: 12px;
	border: 1px solid rgba(198, 128, 255, 0.22);
	background: rgba(176, 38, 255, 0.08);
	text-decoration: none;
	transition:
		border-color 160ms ease,
		background 160ms ease;

	&:hover {
		border-color: rgba(255, 211, 107, 0.42);
		background: rgba(255, 211, 107, 0.08);
	}

	&:hover ${SupportEmail} {
		color: ${THEME.gold};
	}
`;

export const MailIcon = styled.span`
	flex-shrink: 0;
	width: 30px;
	height: 30px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 9px;
	background: rgba(255, 211, 107, 0.1);
	border: 1px solid rgba(255, 211, 107, 0.24);
	color: ${THEME.gold};

	svg {
		width: 15px;
		height: 15px;
		display: block;
	}
`;
